/**
 * Landing route - universal | expat | sme | citizen
 *
 * Phase 3 Track A revamp (2026-05-27, MD-31 Phase 1):
 *   ASKLAW-EMBER-THEME hero swap. S1 hero only - S2-S8 defer Phase 3.
 *   - DROP "hero-editorial" 2px navy left-rule layout (archive comment below).
 *   - ADD centered single-column 760px "hero-ember" voi radial accent tint.
 *   - Display headline 2-line stack: line 1 solid ink + line 2 gradient text.
 *   - CTA pair pill: primary "Bat dau hoi" gradient + secondary "Tim van ban" ghost.
 *   - Citation marker [1] preserved (UPL discipline) -> #cite-1 footnote bottom.
 *   - 3-mode tile section + Stats band + Pricing strip GIU NGUYEN (Phase 1 scope).
 *
 * Per DECISIONS s2026-05-27-UI-REDESIGN-V5-ASKLAW-EMBER-GEMINI-INSPIRED-THEME-SWAP.
 * Spec: specs/domains/frontend/F-FE-THEME-GEMINI.md §6.2 S1 only.
 *
 * Phase 1.5 BOOST (2026-05-27, founder feedback "chua thay animation"):
 *   Upgrade 8 effect homepage cho visible wow:
 *     1. 3 ember blob parallax (scroll scrub) + mouse-follow drift cap 8px.
 *     2. Headline word-by-word stagger 60ms drop-in rotateX 45deg.
 *     3. CountUp "54.323 van ban TW" scroll-trigger once.
 *     4. CTA primary gradient shimmer hover left -> right.
 *     5. Citation [1] hover tooltip vbpl source 200ms slideUp.
 *     6. Scroll progress bar 3px gradient top fixed.
 *     7. Mouse-follow halo radial accent (desktop hover only, low-end skip).
 *     8. 3-mode tile rotateY -15 -> 0 + bounce back.out(1.3) 800ms stagger 150ms.
 *   Reduced-motion guard mandatory. Mobile/touch/low-end skip heavy effect.
 *   GSAP chunk re-used (no new lib). useGsapHeroMount supersede -> useGsapHomepageFx.
 *
 * Previous Direction A hero archive (2026-05-22, supersedes):
 *   DECISIONS s2026-05-22-UI-REDESIGN-V4-DIRECTION-A. Comment block below
 *   preserved cho revert path - Phase 1 reversible via comment swap.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveLanding } from '@core/landings-helpers';
import type { LandingSlug } from '@core/landings-helpers';
import { ogImageMeta } from '@core/landings-helpers';
import { useLang } from '../use-lang';
import { useReveal } from '../lib/use-reveal';
import { Reveal } from '../lib/reveal';
import { animateCount } from '../lib/animate-count';
import { useGsapHomepageFx } from '../lib/use-gsap-homepage-fx';
import { useMouseHalo } from '../lib/use-mouse-halo';
import { useGsapTileTilt } from '../lib/use-gsap-tile-tilt';
import { TrustStrip } from '../components/trust-strip';
import { Button, Chip } from '../components/primitives';

export function LandingRoute({ slug }: { slug: LandingSlug }) {
  const { lang } = useLang();
  const copy = useMemo(() => resolveLanding(slug, lang), [slug, lang]);
  const og = useMemo(() => ogImageMeta(slug), [slug]);

  // Phase 1.5 BOOST: composite homepage fx (word stagger + blob parallax +
  // mouse-follow blob + scroll progress).
  const heroRef = useGsapHomepageFx<HTMLElement>();

  // Phase 1.5 BOOST: mouse-follow halo radial gradient (desktop hover only).
  // Hook attaches to same hero ref via secondary ref pattern (composed below).
  const heroHaloRef = useMouseHalo<HTMLElement>();

  // Phase 1.5 BOOST: 3-mode tile scroll reveal voi rotateY tilt + bounce.
  const modeGridRef = useGsapTileTilt<HTMLElement>('#mode-tiles', {
    selector: '.mode-tile',
    stagger: 0.15,
    duration: 0.8,
    rotateY: -15,
    yOffset: 32,
    start: 'top 80%',
  });

  // Compose 2 ref on hero section (GSAP fx + mouse halo).
  // Both effects need access to the section element.
  const composeHeroRef = (el: HTMLElement | null) => {
    (heroRef as { current: HTMLElement | null }).current = el;
    (heroHaloRef as { current: HTMLElement | null }).current = el;
  };

  useEffect(() => {
    document.title = copy.meta_title;
  }, [copy.meta_title]);

  // Word stagger arrays. Split headline by space. Trailing space preserved
  // via `${w} ` to keep natural rhythm. Keys index-stable across lang switch.
  const headline1Words = (
    lang === 'en' ? 'Understand the law,' : 'Hiểu pháp luật,'
  ).split(' ');
  const headline2Words = (
    lang === 'en' ? 'from what you ask' : 'từ những gì bạn hỏi'
  ).split(' ');

  return (
    <main id="main">
      {/* Phase 1.5 BOOST: scroll progress bar 3px gradient top, fixed
          full-viewport. transform scaleX scrub via useGsapHomepageFx. */}
      <div
        id="scroll-progress"
        className="scroll-progress"
        aria-hidden="true"
        role="presentation"
      />

      <a className="skip-link" href="#features">
        {lang === 'en' ? 'Skip to content' : 'Bỏ qua đến nội dung'}
      </a>

      {/*
        ARCHIVE: Direction A "hero-editorial" 2px navy left-rule layout.
        Preserved for Phase 1 reversible revert path per MD-31 spec.
        Restore by uncommenting + removing hero-ember below.

        <section className="hero-editorial animate-rise" aria-labelledby="hero-heading">
          <div className="editorial-column">
            <p className="eyebrow-editorial">VIETNAMESE LEGAL CORPUS | TƯ LIỆU PHÁP LUẬT VIỆT NAM</p>
            <Chip variant="warning" size="sm">POC</Chip>
            <h1 className="h-display-serif">{copy.hero_headline}<a href="#cite-1">[1]</a></h1>
            <p className="body-lead-editorial">{copy.hero_subhead}</p>
            <Button variant="primary" size="lg">{copy.cta_primary}</Button>
            <Button variant="secondary" size="lg">Đọc bài viết mới</Button>
            ...
          </div>
        </section>
      */}

      {/* ---------- S1 Hero (MD-31 ember, centered 760px) ----------
          Phase 1.5 BOOST: 3 blob + word-stagger + count-up + tooltip +
          mouse halo + CTA shimmer. Composed ref (heroRef + heroHaloRef). */}
      <section
        ref={composeHeroRef}
        className="hero-ember"
        aria-labelledby="hero-heading"
      >
        {/* 3 ember blob - Phase 1.5 BOOST.
            position:absolute filter:blur(80px). Parallax scrub via GSAP. */}
        <div className="hero-ember-blob hero-ember-blob--1" aria-hidden="true" />
        <div className="hero-ember-blob hero-ember-blob--2" aria-hidden="true" />
        <div className="hero-ember-blob hero-ember-blob--3" aria-hidden="true" />

        <p className="hero-ember-eyebrow" data-hero-block>
          {lang === 'en'
            ? 'VIETNAMESE AI LEGAL ASSISTANT'
            : 'TRỢ LÝ PHÁP LÝ AI VIỆT NAM'}
        </p>

        <h1 id="hero-heading" className="hero-ember-headline">
          <span className="hero-ember-headline-line1">
            {headline1Words.map((w, i) => (
              <span key={`l1-${i}-${w}`} className="word" data-word>
                {w}
              </span>
            ))}
          </span>
          <span className="hero-ember-headline-line2">
            {headline2Words.map((w, i) => (
              <span key={`l2-${i}-${w}`} className="word" data-word>
                {w}
              </span>
            ))}
          </span>
        </h1>

        <p className="hero-ember-lead" data-hero-block>
          {lang === 'en'
            ? 'Asklaw cites every Article, Clause and Point from the full Vietnamese legal corpus, kept up to date as new documents take effect.'
            : 'Asklaw trích dẫn từng Điều, Khoản, Điểm từ kho văn bản pháp luật Việt Nam đầy đủ, luôn được cập nhật khi có văn bản mới ban hành.'}
        </p>

        <div className="hero-ember-cta-row" data-hero-block>
          <Link
            to={`/chat?from=landing-${slug}`}
            className="hero-ember-cta-primary"
          >
            <span className="hero-ember-cta-primary-label">
              {lang === 'en' ? 'Start asking' : 'Bắt đầu hỏi'}
            </span>
            <span aria-hidden>{'->'}</span>
          </Link>
          <Link to="/tim-kiem" className="hero-ember-cta-secondary">
            {lang === 'en' ? 'Search documents' : 'Tìm văn bản'}
          </Link>
        </div>

        <p className="hero-ember-sublink" data-hero-block>
          {lang === 'en' ? 'Already have an account?' : 'Đã có tài khoản?'}{' '}
          <Link to="/auth/login?redirect=/chat">
            {lang === 'en' ? 'Sign in' : 'Đăng nhập'} {'->'}
          </Link>
        </p>
      </section>

      {/* ---------- Stats band (Direction A typography) ---------- */}
      <Reveal as="section" aria-label={lang === 'en' ? 'Corpus stats' : 'Thống kê kho văn bản'} className="stats-band">
        <div className="stat-row">
          <Reveal delay={0}>
            <Stat num="54,323" label={lang === 'en' ? 'Central VBPL docs' : 'Văn bản Trung ương'} />
          </Reveal>
          <Reveal delay={80}>
            <Stat num="198,349" label={lang === 'en' ? 'Citation edges' : 'Liên kết trích dẫn'} />
          </Reveal>
          <Reveal delay={160}>
            <Stat num="100%" label={lang === 'en' ? 'Raw HTML coverage' : 'Bao phủ HTML gốc'} />
          </Reveal>
          <Reveal delay={240}>
            <Stat num="< 2s" label={lang === 'en' ? 'Avg response under 2s' : 'Tốc độ trung bình dưới 2s'} />
          </Reveal>
        </div>
      </Reveal>

      {/* ---------- Trust strip (3 gov-grade cards) ---------- */}
      <Reveal
        as="section"
        aria-label={lang === 'en' ? 'Trust signals' : 'Cam kết minh bạch'}
      >
        <TrustStrip lang={lang} />
      </Reveal>

      {/* ---------- 3-mode tile (Ask + Shortcut + Doc Pack) ---------- */}
      <Reveal as="header" className="section-head">
        <p className="eyebrow-editorial">
          {lang === 'en' ? 'THREE MODES' : 'BA CHẾ ĐỘ'}
        </p>
        <h2
          id="features"
          className="h-section-serif"
          style={{ marginTop: 'var(--space-3)' }}
        >
          {lang === 'en'
            ? 'One copilot, three ways to ask Vietnamese law.'
            : 'Một trợ lý, ba cách hỏi pháp luật Việt Nam.'}
        </h2>
        <p
          className="body-lead-editorial"
          style={{ marginTop: 'var(--space-3)', maxWidth: 640 }}
        >
          {lang === 'en'
            ? 'Hỏi Asklaw for free-form question, Shortcut for common scenarios, Doc Pack for curated bundles.'
            : 'Hỏi Asklaw cho câu hỏi tự do, Shortcut cho tình huống thường gặp, Doc Pack cho gói văn bản có chọn lọc.'}
        </p>
      </Reveal>

      {/* Phase 1.5: scroll reveal via useGsapReveal targeting .mode-tile direct */}
      <section
        ref={modeGridRef}
        id="mode-tiles"
        aria-label={lang === 'en' ? 'Three modes' : 'Ba chế độ'}
        className="mode-grid"
      >
        <ModeTile
          icon="?"
          title={lang === 'en' ? 'Hỏi Asklaw' : 'Hỏi Asklaw'}
          body={
            lang === 'en'
              ? 'Chat with the AI legal copilot. Vietnamese-law grounded answers with Article + Clause citations you can verify on vbpl.moj.gov.vn.'
              : 'Trò chuyện với trợ lý AI pháp luật Việt Nam. Trả lời kèm trích dẫn Điều/Khoản bạn có thể kiểm tra trên vbpl.moj.gov.vn.'
          }
          to={`/chat?from=landing-${slug}`}
          ctaLabel={lang === 'en' ? 'Open chat' : 'Mở Chat'}
          badge={lang === 'en' ? 'Live' : 'Sẵn sàng'}
          badgeVariant="success"
        />
        <ModeTile
          icon="S"
          title="Shortcut"
          body={
            lang === 'en'
              ? 'Quick prompt templates for common Vietnamese legal scenarios: labor, land, family, business.'
              : 'Các mẫu câu hỏi nhanh cho tình huống pháp lý thường gặp - lao động, đất đai, hôn nhân, kinh doanh.'
          }
          to="/shortcut"
          ctaLabel={lang === 'en' ? 'See shortcuts' : 'Xem Shortcut'}
          badge={lang === 'en' ? 'Coming soon' : 'Sắp ra mắt'}
          badgeVariant="warning"
        />
        <ModeTile
          icon="D"
          title="Doc Pack"
          body={
            lang === 'en'
              ? 'Curated bundle of Vietnamese legal documents by topic, with usage notes. From 199,000 VND per pack.'
              : 'Gói văn bản pháp luật tổng hợp theo chuyên đề kèm hướng dẫn áp dụng. Từ 199.000 đồng/gói.'
          }
          to="/docpack"
          ctaLabel={lang === 'en' ? 'See Doc Pack' : 'Xem Doc Pack'}
          badge={lang === 'en' ? 'Coming soon' : 'Sắp ra mắt'}
          badgeVariant="warning"
        />
      </section>

      <hr className="section-divider-editorial" />

      {/* ---------- Pricing strip (simplified, no aggressive CTA) ---------- */}
      <Reveal
        as="section"
        aria-label={lang === 'en' ? 'Pricing' : 'Gói sử dụng'}
        className="pricing-strip-editorial"
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'PLANS' : 'GÓI SỬ DỤNG'}
          </p>
          <h2 className="h-section-serif" style={{ marginTop: 'var(--space-2)' }}>
            {copy.pricing_intro}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/chat" style={{ textDecoration: 'none' }}>
            <Button variant="primary" size="lg">
              {lang === 'en' ? 'Start free' : 'Bắt đầu miễn phí'}
              <span aria-hidden style={{ marginLeft: 6 }}>{'->'}</span>
            </Button>
          </Link>
        </div>
      </Reveal>

      <noscript>
        <img src={og.url} alt={og.alt} width={og.width} height={og.height} />
      </noscript>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Internals                                                          */
/* ------------------------------------------------------------------ */

/**
 * Parse a stat string into target value + formatter.
 *   "162,000+" / "4.8M" / ">= 95%" -> animated.
 *   "< 8s" or unparseable          -> static (animate=false).
 */
function parseStatNum(raw: string): {
  value: number;
  format: (n: number) => string;
  animate: boolean;
} {
  const t = raw.trim();
  if (t.startsWith('<') && t[1] !== '=') {
    return { value: 0, format: () => raw, animate: false };
  }
  const pm = t.match(/^([<>]=?|~)\s*/);
  const prefix = pm ? pm[0] : '';
  const rest = pm ? t.slice(pm[0].length) : t;

  const mm = rest.match(/^([\d.]+)M([+]?)$/);
  if (mm) {
    const base = parseFloat(mm[1] ?? '');
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    const tail = mm[2] ?? '';
    return {
      value: Math.round(base * 1e6),
      format: (n) => `${prefix}${(n / 1e6).toFixed(1)}M${tail}`,
      animate: true,
    };
  }
  const pc = rest.match(/^([\d.]+)%$/);
  if (pc) {
    const base = parseFloat(pc[1] ?? '');
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    return { value: base, format: (n) => `${prefix}${Math.round(n)}%`, animate: true };
  }
  const im = rest.match(/^([\d,]+)([+]?)$/);
  if (im) {
    const base = parseInt((im[1] ?? '').replace(/,/g, ''), 10);
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    const tail = im[2] ?? '';
    return {
      value: base,
      format: (n) => `${prefix}${Math.round(n).toLocaleString('en-US')}${tail}`,
      animate: true,
    };
  }
  return { value: 0, format: () => raw, animate: false };
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Stat({ num, label }: { num: string; label: string }) {
  const parsed = useMemo(() => parseStatNum(num), [num]);
  const [ref, revealed] = useReveal<HTMLDivElement>(0.15);
  const [mounted, setMounted] = useState(false);
  const finalDisplay = parsed.animate ? parsed.format(parsed.value) : num;
  const [display, setDisplay] = useState<string>(finalDisplay);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!parsed.animate || !mounted || !revealed) return;
    if (prefersReducedMotion()) {
      setDisplay(parsed.format(parsed.value));
      return;
    }
    setDisplay(parsed.format(0));
    const cleanup = animateCount(0, parsed.value, 1200, (v) => {
      setDisplay(parsed.format(v));
    });
    return cleanup;
  }, [parsed, revealed, mounted]);

  return (
    <div ref={ref}>
      <span className="stat-num">{parsed.animate ? display : num}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function ModeTile({
  icon,
  title,
  body,
  to,
  ctaLabel,
  badge,
  badgeVariant,
}: {
  icon: string;
  title: string;
  body: string;
  to: string;
  ctaLabel: string;
  badge: string;
  badgeVariant: 'success' | 'warning';
}) {
  return (
    <Link to={to} className="mode-tile">
      <div className="mode-tile-head">
        <span className="mode-tile-icon" aria-hidden>
          {icon}
        </span>
        <Chip variant={badgeVariant} size="sm">
          {badge}
        </Chip>
      </div>
      <h3 className="h-tile-serif">{title}</h3>
      <p className="mode-tile-body">{body}</p>
      <span className="mode-tile-cta">
        {ctaLabel}
        <span aria-hidden style={{ marginLeft: 6 }}>{'->'}</span>
      </span>
    </Link>
  );
}
